import ks from "../ReUsableTS/kBrowserShared";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kRelativeTimeFormat, kRelativeTimeFormatCtor } from "./kEditableTableTypes";

// Member bodies of kEditableTableCell moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTableCell member keeps its signature and delegates here.
export class kEditableTableCellPaint {
    // Soft "ghost box" checkbox: a compact rounded tile that reads as filled even
    // when unchecked (kills the whitespace of the old bare glyph), and solidifies
    // to an accent-tinted box with a drawn tick when on. Rendered as inline HTML —
    // exactly like _RadioHtml — so the boolean truth lives in CurrentValue, never
    // in the cell text. role/aria-checked give it real checkbox semantics (the
    // glyph had none) and a stable hook for the E2E suite.
    /** Moved from kEditableTableCell._CheckboxAria (size split); the class member delegates here. */
    public static _CheckboxAria(Self: kEditableTableCell): string {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Label: string = ks.Nz(Self.ColDef.CheckboxLabel);
            if (Label === "") { return ""; }
            return " aria-label=\"" + Label.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;") + "\"";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._CheckboxAria");
            return "";
        }
    }

    // Replay the toggle animation the moment the user flips a checkbox (mouse click
    // or space/F2 — both route through CellClick). Called AFTER SetNewValue has
    // repainted the cell, so the .kEdChk box is a freshly minted node; adding
    // .kEdChkDraw / .kEdChkUndraw makes its CSS keyframes run on first paint. On
    // check the tick draws IN + the box pops; on uncheck the tick reverse-WIPES +
    // the box gives a small settle. Only the interactive path calls this, so bulk
    // repaints (load, paste, fill-down, undo, sort, filter) never animate. Inspired
    // by the "majestic checkbox" CodePen (an all-CSS self-drawing check).
    public static _PlayCheckboxAnim(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Box: Element | null = Self.Cell.querySelector(".kEdChk");
            if (Box === null) { return; }
            if (Self.CurrentValue === true) {
                Box.classList.add("kEdChkDraw");
            } else {
                Box.classList.add("kEdChkUndraw");
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._PlayCheckboxAnim");
            return;
        }
    }

    // Read-mode paint for a Select cell: a single colour-coded chip.
    public static _PaintSelectChip(Self: kEditableTableCell, Value: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Clean: string = ks.Nz(Value);
            Self.Cell.innerHTML = "";
            Self.Cell.style.textAlign = "left";
            if (Clean === "") { return; }
            Self.Cell.appendChild(Self["_MakeChipEl"](Clean));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._PaintSelectChip");
        }
    }

    // Read-mode paint for a SelectMulti cell: one chip per "a; b" value.
    public static _PaintMultiChips(Self: kEditableTableCell, Value: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.innerHTML = "";
            Self.Cell.style.textAlign = "left";
            const Clean: string = ks.Nz(Value);
            if (Clean === "") { return; }
            const Wrap: HTMLSpanElement = document.createElement("span");
            Wrap.style.cssText = "display:inline-flex;flex-wrap:wrap;gap:4px;align-items:center;max-width:100%;vertical-align:middle";
            const Parts: string[] = Clean.split("; ");
            let CurrPart: string;
            for (CurrPart of Parts) {
                const Trimmed: string = CurrPart.trim();
                if (Trimmed !== "") { Wrap.appendChild(Self["_MakeChipEl"](Trimmed)); }
            }
            Self.Cell.appendChild(Wrap);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._PaintMultiChips");
        }
    }

    // Read-mode paint for a Color cell: a rounded swatch, with BOTH hex and rgb in
    // the cell's hover tooltip (no visible label). Replaces the old full-cell
    // background flood — so Color harvest now reads CurrentValue, not the cell
    // background (see _ConvertByColType).
    // Value is deliberately NOT typed `string`. It used to be, and both call sites
    // (SetCellValue and SetCellOrgValue) hand it `ks.Nz(<cell value>)`, which is `any` - so a
    // Color column holding a number reached `Clean.charAt(0)` and threw
    // "TypeError: r.charAt is not a function". The global handler then set KErrorMode, which
    // short-circuits every guarded method, so the whole surface died until reload. Found
    // 2026-08-03 by opening the Kanban Task Manager settings dialog; fixed here rather than at
    // the two call sites because this is the one place that requires a string, and coercing
    // here protects every future caller too. Rendering whatever the cell holds is this
    // method's job, so the conversion is a real fix, not a suppression.
    public static _PaintColorSwatch(Self: kEditableTableCell, Value: string | number | boolean | null | undefined): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Raw: string | number | boolean | null | undefined = ks.Nz(Value as string);
            let Clean = "";
            if (Raw !== null && Raw !== undefined) { Clean = String(Raw).trim(); }
            Self.Cell.innerHTML = "";
            Self.Cell.style.textAlign = "center";
            Self.Cell.style.backgroundColor = "";
            if (Clean === "") { Self.Cell.removeAttribute("title"); return; }
            const Swatch: HTMLSpanElement = document.createElement("span");
            Swatch.style.cssText = "display:inline-block;width:20px;height:20px;border-radius:5px;border:1px solid rgba(0,0,0,0.15);box-sizing:border-box;vertical-align:middle";
            Swatch.style.background = Clean;
            Self.Cell.appendChild(Swatch);
            if (Clean.charAt(0) === "#") {
                Self.Cell.title = Clean + "  ·  rgb(" + ks.GetRGBCodeFromHex(Clean) + ")";
            } else {
                Self.Cell.title = Clean;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._PaintColorSwatch");
        }
    }

    // Paint a faint magnitude data-bar behind a Number/Currency cell, scaled to the
    // column max (supplied by the table-level pass). Grows from the RIGHT edge so it
    // aligns with the right-aligned digits it measures.
    public static _PaintDataBar(Self: kEditableTableCell, ColumnMax: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Value: number = Math.abs(ks.N0(Self.CurrentValue));
            if (ColumnMax <= 0 || Value <= 0) {
                Self.Cell.style.backgroundImage = "";
                return;
            }
            let Pct: number = Math.round((Value / ColumnMax) * 100);
            if (Pct > 100) { Pct = 100; }
            const Tint: string = "rgba(0,120,212,0.22)";
            Self.Cell.style.backgroundImage = "linear-gradient(to left, " + Tint + " 0%, " + Tint + " " + Pct + "%, transparent " + Pct + "%)";
            Self.Cell.style.backgroundRepeat = "no-repeat";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._PaintDataBar");
        }
    }

    // Add a locale-aware relative-time hover tooltip to a Date cell ("in 3 days",
    // "2 days ago", "today"). Informational only — the visible short-date text is
    // unchanged, so the cell's MEANING is not altered.
    public static _ApplyDateRelativeTooltip(Self: kEditableTableCell, Value: any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!Value || typeof Value.ToJsDate !== "function") { Self.Cell.removeAttribute("title"); return; }
            const Ctor: kRelativeTimeFormatCtor | undefined = (Intl as unknown as { RelativeTimeFormat?: kRelativeTimeFormatCtor }).RelativeTimeFormat;
            if (Ctor === undefined) { return; }
            const MsPerDay: number = 86400000;
            const Target: Date = Value.ToJsDate();
            const Now: Date = new Date();
            const TargetDay: number = Math.floor(new Date(Target.getFullYear(), Target.getMonth(), Target.getDate()).getTime() / MsPerDay);
            const NowDay: number = Math.floor(new Date(Now.getFullYear(), Now.getMonth(), Now.getDate()).getTime() / MsPerDay);
            const Formatter: kRelativeTimeFormat = new Ctor(undefined, { numeric: "auto" });
            Self.Cell.title = Formatter.format(TargetDay - NowDay, "day");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellPaint._ApplyDateRelativeTooltip");
        }
    }
}
